import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import bemify from 'utils/bemify';
import { googleMapsSearchParamAddress } from 'utils/restaurant-addresses-helpers';

// Components
import Button from 'components/button';
import Icon from 'components/icon';
import Image from 'components/image';
import Modal from 'components/modal';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const RestaurantInfoModal = ({
  className = null,
  restaurantInfo = null,
  show = false,
  translations,
  onClose = () => {},
  weekSchedule = null
}) => {
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  const {
    address,
    category,
    facebookHandle,
    instagramHandle,
    logoUrl,
    name,
    phoneNumber,
    twitterHandle
  } = restaurantInfo;

  const socialMedia = useMemo(() => {
    const socials = [];

    if (facebookHandle) {
      const facebookSocialDetails = {
        name: 'facebook',
        text: facebookHandle,
        url: `https://facebook.com/${facebookHandle}`
      };

      socials.push(facebookSocialDetails);
    }

    if (instagramHandle) {
      const instagramSocialDetails = {
        name: 'instagram',
        text: instagramHandle,
        url: `https://instagram.com/${instagramHandle}`
      };

      socials.push(instagramSocialDetails);
    }

    if (twitterHandle) {
      const twitterSocialDetails = {
        name: 'twitter',
        text: twitterHandle,
        url: `https://twitter.com/${twitterHandle}`
      };

      socials.push(twitterSocialDetails);
    }

    return socials;
  }, [facebookHandle, instagramHandle, twitterHandle]);

  if (!restaurantInfo) return null;

  const scheduleText = restaurantInfo.isOpen ? translations.open : translations.closed;

  const hasAddress = !!address;
  const hasOtherAddresses = Array.isArray(restaurantInfo.addresses) && !!restaurantInfo.addresses.length;
  const hasPhoneNumber = !!phoneNumber;
  const hasSchedule = Array.isArray(weekSchedule) && !!weekSchedule.length;

  const addresses = [];

  if (hasAddress) {
    addresses.push({
      address,
      // Other addresses have a database ID, but the primary address does not
      // 1 is safe because the database will never have an address with ID 1
      id: 1
    });
  }

  if (hasOtherAddresses) {
    addresses.push(...restaurantInfo.addresses);
  }

  const handleShowFullScheduleButtonClick = () => {
    setShowFullSchedule(prevState => !prevState);
  };

  const formatHour = (dateObject) => {
    if (dateObject instanceof Date) {
      const hours = dateObject.getHours();

      return hours < 10 ? `0${hours}` : hours;
    }

    return dateObject;
  };

  const formatMinutes = (dateObject) => {
    if (dateObject instanceof Date) {
      const minutes = dateObject.getMinutes();

      return minutes < 10 ? `0${minutes}` : minutes;
    }

    return dateObject;
  };

  const modalHeader = (
    <div className={styles.restaurantInfoModal__header}>
      {!!logoUrl && (
        <div className={styles.restaurantInfoModal__logo}>
          <Image
            alt={translations.logoAlt}
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            sizes="(min-width: 992px) 64px, 48px"
            src={logoUrl}
          />
        </div>
      )}
      {(!!name || !!category) && (
        <div className={styles.restaurantInfoModal__textWrapper}>
          {!!name && (
            <div
              className={styles.restaurantInfoModal__name}
              title={name}
            >
              {name}
            </div>
          )}
          {!!category && (
            <div
              className={styles.restaurantInfoModal__text}
              title={category}
            >
              {category}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      ariaTitle={name}
      className={`${styles.restaurantInfoModal}${className ? ` ${className}` : ''}`}
      customHeader={modalHeader}
      headerClassName={styles.restaurantInfoModal__modalHeader}
      mobilePosition="bottom"
      show={show}
      size="l"
      onClose={onClose}
    >
      <div className={styles.restaurantInfoModal__content}>
        {(hasAddress || hasPhoneNumber || hasSchedule) && (
          <div className={styles.restaurantInfoModal__restaurantInfo}>
            <div className={styles.restaurantInfoModal__contentTitle}>
              {translations.information}
            </div>
            {addresses.length && addresses.map(addressEntry => (
              <div
                key={addressEntry.id}
                className={styles.restaurantInfoModal__contentItem}
              >
                <div className={styles.restaurantInfoModal__contentItemIconWrapper}>
                  <Icon name="address" />
                </div>
                <Button
                  className={styles.restaurantInfoModal__button}
                  external
                  href={`https://www.google.com/maps/place/${googleMapsSearchParamAddress(addressEntry.address)}`}
                  noBackground
                  paddingSize="none"
                  text={`${addressEntry.name ? `${addressEntry.name} - ` : ''} ${addressEntry.address}`}
                  textWeight="regular"
                  theme="unstyled"
                />
              </div>
            ))}
            {hasPhoneNumber && (
              <div className={styles.restaurantInfoModal__contentItem}>
                <div className={styles.restaurantInfoModal__contentItemIconWrapper}>
                  <Icon name="phone" />
                </div>
                <Button
                  className={styles.restaurantInfoModal__button}
                  href={`tel:${phoneNumber}`}
                  noBackground
                  paddingSize="none"
                  text={phoneNumber}
                  textWeight="regular"
                  theme="unstyled"
                />
              </div>
            )}
            {hasSchedule && (
              <>
                <div className={styles.restaurantInfoModal__contentItem}>
                  <div className={styles.restaurantInfoModal__contentItemIconWrapper}>
                    <Icon name="clock" />
                  </div>
                  <div title={scheduleText}>
                    {scheduleText}
                  </div>
                  <div className={styles.restaurantInfoModal__contentItemDetailWrapper}>
                    <Button
                      className={styles.restaurantInfoModal__showFullScheduleButton}
                      icon={showFullSchedule ? 'chevron-up' : 'chevron-down'}
                      iconClassName={styles.restaurantInfoModal__showFullScheduleButtonIcon}
                      iconSize="s"
                      noBackground
                      paddingSize="none"
                      text={showFullSchedule ? translations.scheduleSeeLess : translations.scheduleSeeMore}
                      textWeight="regular"
                      theme="unstyled"
                      title={showFullSchedule ? translations.scheduleSeeLess : translations.scheduleSeeMore}
                      onClick={handleShowFullScheduleButtonClick}
                    />
                  </div>
                </div>
                {showFullSchedule && (
                  <div className={styles.restaurantInfoModal__scheduleWrapper}>
                    {weekSchedule.map((entry, index) => (
                      <div key={index}>
                        <div className={styles.restaurantInfoModal__scheduleWeekdayText}>
                          {translations.weekdays[entry.day.toLowerCase()]}
                        </div>
                        {!entry.closedEvent && Array.isArray(entry.timeEntries) && entry.timeEntries.length ?
                          (
                            <div className="d-flex align-items-center">
                              {entry.timeEntries.map((timeEntry, timeEntryIndex) => (
                                <React.Fragment key={timeEntryIndex}>
                                  <div className={styles.restaurantInfoModal__contentItemDetailText}>
                                    {formatHour(timeEntry.openTime)}
                                      :
                                    {formatMinutes(timeEntry.openTime)}
                                      h
                                    {' '}
                                      -
                                    {' '}
                                    {formatHour(timeEntry.closeTime)}
                                      :
                                    {formatMinutes(timeEntry.closeTime)}
                                      h
                                  </div>
                                  {timeEntryIndex < entry.timeEntries.length - 1 && (
                                    <div className={styles.restaurantInfoModal__contentItemDetailEllipse} />
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          ) :
                          (
                            <div className={styles.restaurantInfoModal__contentItemDetailText}>
                              {translations.closed}
                              {!!entry.closedEvent && ` - ${entry.closedEvent}`}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {!!socialMedia.length && (
          <div className={styles.restaurantInfoModal__restaurantInfo}>
            <div className={styles.restaurantInfoModal__contentTitle}>
              {translations.socialMedia}
            </div>
            {socialMedia.map((entry, index) => (
              <div
                key={index}
                className={styles.restaurantInfoModal__contentItem}
              >
                <div className={styles.restaurantInfoModal__contentItemIconWrapper}>
                  <Icon name={entry.name} />
                </div>
                <Button
                  className={styles.restaurantInfoModal__button}
                  href={entry.url}
                  noBackground
                  paddingSize="none"
                  target="_blank"
                  text={entry.text}
                  textWeight="regular"
                  theme="unstyled"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

RestaurantInfoModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  restaurantInfo: PropTypes.shape({
    address: PropTypes.string,
    category: PropTypes.string,
    cover: PropTypes.string,
    facebookHandle: PropTypes.string,
    instagramHandle: PropTypes.string,
    isOpen: PropTypes.bool,
    logoUrl: PropTypes.string,
    name: PropTypes.string,
    phoneNumber: PropTypes.string,
    twitterHandle: PropTypes.string
  }),
  show: PropTypes.bool,
  translations: PropTypes.shape({
    closed: PropTypes.string,
    information: PropTypes.string,
    logoAlt: PropTypes.string,
    open: PropTypes.string,
    scheduleSeeLess: PropTypes.string,
    scheduleSeeMore: PropTypes.string,
    socialMedia: PropTypes.string,
    weekdays: PropTypes.shape({
      friday: PropTypes.string,
      monday: PropTypes.string,
      saturday: PropTypes.string,
      sunday: PropTypes.string,
      thursday: PropTypes.string,
      tuesday: PropTypes.string,
      wednesday: PropTypes.string
    }).isRequired
  }).isRequired,
  weekSchedule: PropTypes.arrayOf(PropTypes.shape({
    closeTime: PropTypes.string,
    openTime: PropTypes.string
  }))
};

export default RestaurantInfoModal;
